import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NIVELES = ["A1", "A2", "B1", "B2", "C1"];

const TXT = {
  en: {
    title: "Create your access", name: "Full name", email: "Email", phone: "WhatsApp",
    optional: "Optional", level: "Your Spanish level", notsure: "I am not sure yet",
    maillang: "Language for our emails", start: (d) => `Start my ${d} free days`,
    nocard: "No card required. Nothing is charged automatically.",
    check: "Check your email",
    sent: (m) => <>We sent a 6-digit code to <b>{m}</b>. Type it here and you are in, no need to enter your email again.</>,
    enter: "Enter WSS GO", spam: "Did not get it? Check your spam folder.",
    again: "Send it to me again", wait: (s) => `Send it again in ${s}s`,
    need: "We need your name and your email",
    failed: "We could not create your access, please try again",
    badcode: "That code is not valid or it has expired",
    resent: "We sent you a new code. Check your email.",
    throttled: "We already sent you several codes. Please use the last one or wait a few minutes.",
    resendfail: "We could not send the code again, please try in a moment",
  },
  es: {
    title: "Creá tu acceso", name: "Nombre completo", email: "Correo", phone: "WhatsApp",
    optional: "Opcional", level: "Tu nivel de español", notsure: "Todavía no sé",
    maillang: "Idioma de nuestros correos", start: (d) => `Empezar mis ${d} días gratis`,
    nocard: "No se pide tarjeta. No se cobra nada automáticamente.",
    check: "Revisá tu correo",
    sent: (m) => <>Te mandamos un código de 6 dígitos a <b>{m}</b>. Escribilo acá y entrás, sin tener que poner tu correo de nuevo.</>,
    enter: "Entrar a WSS GO", spam: "¿No te llegó? Revisá la carpeta de spam.",
    again: "Mandámelo otra vez", wait: (s) => `Mandámelo otra vez en ${s}s`,
    need: "Necesitamos tu nombre y tu correo",
    failed: "No pudimos crear tu acceso, intentá otra vez",
    badcode: "Ese código no es válido o ya venció",
    resent: "Te mandamos un código nuevo. Revisá tu correo.",
    throttled: "Ya te mandamos varios códigos. Usá el último o esperá unos minutos.",
    resendfail: "No pudimos mandar el código otra vez, probá en un momento",
  },
};

/** Tarjeta de registro de WSS Live. Se usa en la página pública y, sola, en el
 *  `iframe` que la escuela pega en su propio sitio. */
export const LivePassSignupCard = ({ trialDays, source = "wss_live_page", lang = "en", accent = false,
                                    escapeOnDone = false, initialEmail = "", initialDone = false }) => {
  const x = TXT[lang] || TXT.en;
  // En la landing el botón va naranja, como el resto de los llamados a la acción.
  const btn = accent
    ? "bg-[#ff731d] hover:bg-[#e05d0c] shadow-lg shadow-orange-500/25"
    : "bg-[#0d1b2a] hover:bg-[#15293d]";
  const [form, setForm] = useState({ name: "", email: initialEmail, phone: "", level: "", language: lang });
  const [dias, setDias] = useState(trialDays);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(initialDone);
  const [code, setCode] = useState("");
  const [entering, setEntering] = useState(false);
  // Reenvío del código: con cuenta regresiva para que no lo pidan en ráfaga.
  const [espera, setEspera] = useState(0);

  useEffect(() => {
    if (espera <= 0) return;
    const id = setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [espera]);

  // Si la persona cambia el idioma de la página, el idioma de los correos la sigue.
  useEffect(() => {
    setForm((f) => ({ ...f, language: lang }));
  }, [lang]);

  const reenviar = async () => {
    setEspera(45);
    try {
      const { data } = await axios.post(`${API}/auth/magic-link/request`,
                                        { email: form.email.trim().toLowerCase(), method: "code" });
      toast.success(data?.throttled ? x.throttled : x.resent);
    } catch {
      toast.error(x.resendfail);
    }
  };

  useEffect(() => {
    if (trialDays) return setDias(trialDays);
    axios.get(`${API}/public/live/sessions`, { params: { limit: 1 } })
      .then((r) => setDias(r.data?.trial_days ?? 7))
      .catch(() => setDias(7));
  }, [trialDays]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(x.need);
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/public/live/signup`, { ...form, source });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || x.failed);
    } finally {
      setSending(false);
    }
  };

  /* El código se verifica acá mismo: mandarlo al login lo obligaría a escribir
     su correo otra vez y a pedir un segundo código. Dentro de un iframe ajeno
     la cookie de sesión no sobrevive, así que el backend devuelve un pase de un
     solo uso y recién ahí saltamos a la plataforma, ya con la sesión puesta. */
  const entrar = async (e) => {
    e.preventDefault();
    setEntering(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/magic-link/verify-code`,
        { email: form.email.trim().toLowerCase(), code, embed: escapeOnDone },
        { withCredentials: !escapeOnDone },
      );
      const destino = escapeOnDone && data?.handoff
        ? `${window.location.origin}/auth/magic?token=${encodeURIComponent(data.handoff)}&next=/dashboard`
        : `${window.location.origin}/dashboard`;
      window.open(destino, "_top");
    } catch (err) {
      toast.error(err?.response?.data?.detail || x.badcode);
      setEntering(false);
    }
  };

  if (done) {
    return (
      <div className="py-2" data-testid="live-public-done">
        <div className="h-12 w-12 rounded-full bg-emerald-50 grid place-items-center mx-auto">
          <Check className="h-6 w-6 text-emerald-600" />
        </div>
        <h3 className="font-display text-lg font-bold mt-3 text-center">{x.check}</h3>
        <p className="text-[13px] text-slate-600 mt-1.5 text-center">{x.sent(form.email)}</p>
        <form onSubmit={entrar} className="mt-4 space-y-3">
          <Input
            data-testid="live-public-code"
            value={code} inputMode="numeric" maxLength={6} autoFocus placeholder="000000"
            className="text-center text-2xl tracking-[0.4em] font-bold h-14"
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />
          <Button type="submit" disabled={entering || code.length !== 6}
                  className={`w-full rounded-full font-semibold ${btn}`}
                  data-testid="live-public-enter">
            {entering ? <Loader2 className="h-4 w-4 animate-spin" /> : x.enter}
          </Button>
        </form>
        <p className="text-[11px] text-slate-400 text-center mt-3">
          {x.spam}
        </p>
        <div className="text-center mt-1">
          <button type="button" onClick={reenviar} disabled={espera > 0}
                  className="text-[12px] font-semibold text-[#0d1b2a] underline underline-offset-2 disabled:text-slate-400 disabled:no-underline"
                  data-testid="live-public-resend">
            {espera > 0 ? x.wait(espera) : x.again}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3.5" data-testid="live-public-form">
      <h3 className="font-display text-lg font-bold">{x.title}</h3>
      <div>
        <Label htmlFor="lp-name">{x.name}</Label>
        <Input id="lp-name" value={form.name} className="mt-1"
               onChange={(e) => setForm({ ...form, name: e.target.value })}
               data-testid="live-public-name" />
      </div>
      <div>
        <Label htmlFor="lp-email">{x.email}</Label>
        <Input id="lp-email" type="email" value={form.email} className="mt-1"
               onChange={(e) => setForm({ ...form, email: e.target.value })}
               data-testid="live-public-email" />
      </div>
      <div>
        <Label htmlFor="lp-phone">{x.phone}</Label>
        <Input id="lp-phone" value={form.phone} className="mt-1" placeholder={x.optional}
               onChange={(e) => setForm({ ...form, phone: e.target.value })}
               data-testid="live-public-phone" />
      </div>
      <div>
        <Label>{x.level}</Label>
        <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
          <SelectTrigger className="mt-1" data-testid="live-public-level">
            <SelectValue placeholder={x.notsure} />
          </SelectTrigger>
          <SelectContent>
            {NIVELES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>{x.maillang}</Label>
        <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
          <SelectTrigger className="mt-1" data-testid="live-public-language">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="en">English</SelectItem>
            <SelectItem value="es">Español</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" disabled={sending}
              className={`w-full rounded-full font-semibold ${btn}`}
              data-testid="live-public-submit">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : x.start(dias || 7)}
      </Button>
      <p className="text-[11px] text-slate-400 text-center">
        {x.nocard}
      </p>
    </form>
  );
};
